///
/// EarthBrowser
///
/// Created by Matt Giger
/// Copyright (c) 2025 Geodata Labs
///

import {
	ContinuousGestureRecognizer,
	GestureState,
	type GestureEventData,
	type GestureRecognizerConfig,
	type KeyState,
	type TouchPoint
} from "./GestureRecognizer"

export interface PanGestureEventData extends GestureEventData {
	// Translation from start position
	translationX: number
	translationY: number
	// Current velocity (pixels per second)
	velocityX: number
	velocityY: number
	// Number of touches currently down
	touchCount: number
	// Modifier key state
	keys: KeyState
}

export interface PanGestureConfig extends GestureRecognizerConfig {
	// Minimum movement before pan is recognized (in pixels)
	minimumMovement?: number
	// Minimum movement for multi-touch (2+) before pan is recognized (in pixels).
	// Higher than minimumMovement to give zoom/rotate time to discriminate.
	// Default: same as minimumMovement
	multiTouchMinimumMovement?: number
	// Max scale change allowed before failing for multi-touch gestures.
	// If finger distance ratio changes more than this, it's a pinch not a pan.
	// Default: 0.08 (8%)
	multiTouchScaleTolerance?: number
	// Max rotation allowed (radians) before failing for multi-touch gestures.
	// If angle between fingers changes more than this, it's a twist not a pan.
	// Default: 0.12 (~7 degrees)
	multiTouchRotationTolerance?: number
}

/**
 * Recognizes pan (drag) gestures
 *
 * Tracks translation from start point and velocity.
 * Supports single and multi-touch panning.
 */
export class PanGestureRecognizer extends ContinuousGestureRecognizer<PanGestureEventData> {
	private minimumMovement: number
	private multiTouchMinimumMovement: number
	private multiTouchScaleTolerance: number
	private multiTouchRotationTolerance: number

	private startLocation?: { x: number, y: number }
	private lastLocation?: { x: number, y: number }
	private lastMoveTime: number = 0
	private velocityX: number = 0
	private velocityY: number = 0

	// Multi-touch discrimination state
	private initialFingerDistance: number = 0
	private initialFingerAngle: number = 0

	// Velocity: centroid samples over a short trailing window, timed by the
	// events themselves (coalesced moves arrive in bursts, so wall-clock
	// deltas between them are ~0 and would blow up a per-event estimate)
	private samples: Array<{ x: number, y: number, t: number }> = []
	private readonly velocityWindowMs = 100
	// A pointer that paused this long before lifting has no release velocity
	private readonly velocityStaleMs = 100

	// Re-arm settle: when this recognizer adopts touches mid-session (e.g. one
	// finger remaining as a pinch ends), the remaining finger usually drifts a
	// few pixels while the other finger lifts. Ignore that residual movement
	// for a short window so the end of a pinch doesn't kick off a stray pan.
	private rearmTime = 0
	private readonly rearmSettleMs = 150

	constructor(config: PanGestureConfig = {}) {
		super(config)
		this.minimumMovement = config.minimumMovement ?? 10
		this.multiTouchMinimumMovement = config.multiTouchMinimumMovement ?? (config.minimumMovement ?? 10)
		this.multiTouchScaleTolerance = config.multiTouchScaleTolerance ?? 0.08
		this.multiTouchRotationTolerance = config.multiTouchRotationTolerance ?? 0.12
	}

	get gestureType(): string {
		return "pan"
	}

	handleTouchStart(touches: TouchPoint[]): void {
		if (!this._enabled) return

		// Update tracked touches
		touches.forEach(t => this.touches.set(t.id, t))

		// Check touch count requirements
		if (this.touchCount < this._minimumTouches || this.touchCount > this._maximumTouches) {
			if (this.isActive()) {
				this.cancel()
			} else if (!this.isTerminal()) {
				this.fail()
			}
			return
		}

		const centroid = this.getCentroid()

		if (!this.isActive()) {
			// New pan gesture
			this.startLocation = centroid
			this.lastLocation = centroid
			this.lastMoveTime = performance.now()
			this.velocityX = 0
			this.velocityY = 0
			this.samples = []
			this.rearmTime = 0

			// Record initial finger geometry for multi-touch discrimination
			if (this.touchCount >= 2) {
				const touchArray = Array.from(this.touches.values())
				const dx = touchArray[1].x - touchArray[0].x
				const dy = touchArray[1].y - touchArray[0].y
				this.initialFingerDistance = Math.sqrt(dx * dx + dy * dy)
				this.initialFingerAngle = Math.atan2(dy, dx)
			}
		} else {
			// Additional touch added during pan - update start for relative tracking
			// We keep the gesture going but adjust the reference point
			this.lastLocation = centroid
			this.lastMoveTime = performance.now()
		}
	}

	handleTouchMove(touches: TouchPoint[]): void {
		if (!this._enabled) return

		// Update tracked touches including key state, adopting any the arena
		// sends that we lost to a mid-session cancel/fail. This lets panning
		// resume with the remaining finger after a pinch ends, instead of
		// staying dead until the user lifts and re-touches.
		touches.forEach(t => {
			const existing = this.touches.get(t.id)
			if (existing) {
				existing.x = t.x
				existing.y = t.y
				existing.keys = t.keys
			} else {
				this.touches.set(t.id, t)
			}
		})

		// Check touch count requirements
		if (this.touchCount < this._minimumTouches || this.touchCount > this._maximumTouches) {
			// De-arm while the touch configuration is invalid (e.g. a second
			// finger is down during a pinch). Any previously captured anchor is
			// stale by the time the count returns to range - without this, the
			// pan could begin with a huge translation jump measured from a
			// position recorded mid-pinch. A fresh anchor (and settle window)
			// is captured when the count is valid again.
			if (!this.isActive()) {
				this.startLocation = undefined
			}
			return
		}

		const centroid = this.getCentroid()
		const now = PanGestureRecognizer.eventTime(touches)

		// Re-arm after a reset while touches are still down: anchor the pan at
		// the current centroid and start tracking from here
		if (!this.startLocation) {
			this.startLocation = centroid
			this.lastLocation = centroid
			this.lastMoveTime = now
			this.velocityX = 0
			this.velocityY = 0
			this.samples = []
			this.rearmTime = now
			if (this.touchCount >= 2) {
				const touchArray = Array.from(this.touches.values())
				const dx = touchArray[1].x - touchArray[0].x
				const dy = touchArray[1].y - touchArray[0].y
				this.initialFingerDistance = Math.sqrt(dx * dx + dy * dy)
				this.initialFingerAngle = Math.atan2(dy, dx)
			}
			return
		}

		// While settling after a re-arm, keep re-anchoring instead of counting
		// the lift transient toward the pan threshold. A deliberate continued
		// drag just starts measuring ~150ms later, which is imperceptible.
		if (!this.isActive() && this.rearmTime > 0 && now - this.rearmTime < this.rearmSettleMs) {
			this.startLocation = centroid
			this.lastLocation = centroid
			this.lastMoveTime = now
			return
		}

		this.updateVelocity(centroid, now)

		this.lastLocation = centroid
		this.lastMoveTime = now

		if (!this.isActive() && this.startLocation) {
			// For multi-touch, check if scale or rotation indicates pinch/twist
			if (this.touchCount >= 2 && this.initialFingerDistance > 0) {
				const touchArray = Array.from(this.touches.values())
				const fdx = touchArray[1].x - touchArray[0].x
				const fdy = touchArray[1].y - touchArray[0].y
				const fingerDist = Math.sqrt(fdx * fdx + fdy * fdy)
				const fingerAngle = Math.atan2(fdy, fdx)

				const scale = fingerDist / this.initialFingerDistance
				const scaleChange = Math.abs(scale - 1)
				let rotation = fingerAngle - this.initialFingerAngle
				while (rotation > Math.PI) rotation -= 2 * Math.PI
				while (rotation < -Math.PI) rotation += 2 * Math.PI

				if (scaleChange > this.multiTouchScaleTolerance || Math.abs(rotation) > this.multiTouchRotationTolerance) {
					this.fail()
					return
				}
			}

			const dx = centroid.x - this.startLocation.x
			const dy = centroid.y - this.startLocation.y
			const distance = Math.sqrt(dx * dx + dy * dy)
			const effectiveMinimum = this.touchCount >= 2 ? this.multiTouchMinimumMovement : this.minimumMovement

			if (distance >= effectiveMinimum) {
				const data = this.createEventData(GestureState.Began)
				this.begin(data)
			}
		} else if (this.isActive()) {
			// Emit change event
			const data = this.createEventData(GestureState.Changed)
			this.change(data)
		}
	}

	handleTouchEnd(touches: TouchPoint[]): void {
		if (!this._enabled) return

		// Remove ended touches
		touches.forEach(t => this.touches.delete(t.id))

		// Check if we've dropped below minimum touches
		if (this.isActive() && this.touchCount < this._minimumTouches) {
			// A pointer that stopped before lifting releases with no velocity
			const last = this.samples[this.samples.length - 1]
			if (!last || PanGestureRecognizer.eventTime(touches) - last.t > this.velocityStaleMs) {
				this.velocityX = 0
				this.velocityY = 0
			}
			// End the gesture
			const data = this.createEventData(GestureState.Ended)
			this.end(data)
		} else if (!this.isActive() && this.touchCount === 0 && !this.isTerminal()) {
			// Never started - fail
			this.fail()
		} else if (this.touchCount > 0) {
			// Still have touches - update last location
			const centroid = this.getCentroid()
			this.lastLocation = centroid
			this.lastMoveTime = performance.now()
		}
	}

	handleTouchCancel(): void {
		this.cancel()
	}

	/** Timestamp of a batch of touches (the latest), on the performance.now() timeline */
	private static eventTime(touches: TouchPoint[]): number {
		let t = 0
		for (const touch of touches) t = Math.max(t, touch.timestamp)
		return t > 0 ? t : performance.now()
	}

	/**
	 * Velocity as the secant over the trailing window of centroid samples:
	 * stable across refresh rates and event coalescing, and it reads the
	 * finger's motion just before lift rather than any single noisy pair
	 */
	private updateVelocity(centroid: { x: number, y: number }, now: number): void {
		this.samples.push({ x: centroid.x, y: centroid.y, t: now })
		while (this.samples.length > 2 && now - this.samples[0].t > this.velocityWindowMs) {
			this.samples.shift()
		}
		const first = this.samples[0]
		const last = this.samples[this.samples.length - 1]
		const dt = (last.t - first.t) / 1000
		if (dt > 0) {
			this.velocityX = (last.x - first.x) / dt
			this.velocityY = (last.y - first.y) / dt
		}
	}

	protected createEventData(state: GestureState): PanGestureEventData {
		const centroid = this.touchCount > 0 ? this.getCentroid() : (this.lastLocation ?? { x: 0, y: 0 })
		const start = this.startLocation ?? centroid

		// Get keys from first touch
		const firstTouch = this.touches.values().next().value as TouchPoint | undefined
		const keys: KeyState = firstTouch?.keys ?? {
			ctrlKey: false,
			shiftKey: false,
			altKey: false,
			metaKey: false
		}

		return {
			type: "pan",
			state,
			timestamp: performance.now(),
			x: centroid.x,
			y: centroid.y,
			ownerHits: this.getOwnerHits(),
			translationX: centroid.x - start.x,
			translationY: centroid.y - start.y,
			velocityX: this.velocityX,
			velocityY: this.velocityY,
			touchCount: this.touchCount,
			keys
		}
	}

	// Convenience method to reset translation (for relative panning)
	public setTranslation(x: number, y: number): void {
		const centroid = this.getCentroid()
		this.startLocation = {
			x: centroid.x - x,
			y: centroid.y - y
		}
	}

	override reset(): void {
		super.reset()
		this.startLocation = undefined
		this.lastLocation = undefined
		this.lastMoveTime = 0
		this.velocityX = 0
		this.velocityY = 0
		this.samples = []
		this.initialFingerDistance = 0
		this.initialFingerAngle = 0
		this.rearmTime = 0
	}
}

